import type {
  DealSubmissionRichDto,
  DealSubmissionSectionDto,
  PortalDealSubmissionReturnCommentDto,
} from "../../api/types";
import { outstandingEvaluationNames } from "./evaluationFreshness";
import { sectionKey, REVIEW_SECTION_KEY } from "./DealSubmissionRunner";

// Wizard left rail — one row per section + a final "Review & Submit".
// Per-section status mirrors the backend submit gate closely enough for
// navigation: required docs uploaded, section state saved Complete, and
// applicable Evaluate & Feedback runs current.

interface ProgressRailProps {
  data: DealSubmissionRichDto;
  sections: DealSubmissionSectionDto[];
  activeKey: string;
  onJump: (key: string) => void;
  commentsBySection?: Record<string, PortalDealSubmissionReturnCommentDto[]>;
}

export function ProgressRail({ data, sections, activeKey, onJump, commentsBySection = {} }: ProgressRailProps) {
  // RECON→FIX (UAT 2026-05-30) — overall "{done} of {total} complete"
  // progress at the top of the rail. Total counts the template sections
  // (the Review step isn't a section to complete).
  const total = sections.length;
  const done = sections.filter(
    (s) => sectionDisplayStatus(s, data) === "Complete",
  ).length;

  return (
    // Sticky rail so the step list + progress stay visible as the form
    // scrolls. top-space-5 clears the chrome's content padding.
    <nav
      aria-label="Submission sections"
      data-testid="wizard-rail"
      className="md:sticky md:top-space-5"
    >
      <div className="mb-space-3" data-testid="wizard-progress">
        <span className="text-caption font-medium uppercase tracking-label text-gray-mid">
          {done} of {total} complete
        </span>
        <div className="mt-space-1 h-1 w-full overflow-hidden rounded-full bg-white-warm">
          <div
            className="h-full rounded-full bg-gold transition-all duration-normal"
            style={{ width: total > 0 ? `${(done / total) * 100}%` : "0%" }}
          />
        </div>
      </div>

      <ol className="list-none p-0 m-0 flex flex-col gap-space-2">
        {sections.map((s) => {
          const key = sectionKey(s);
          const status = sectionDisplayStatus(s, data);
          const unresolvedComments = (commentsBySection[s.id] ?? []).filter((c) => !c.resolved).length;
          return (
            <li key={s.id}>
              <button
                type="button"
                onClick={() => onJump(key)}
                aria-current={key === activeKey ? "step" : undefined}
                data-testid={`wizard-rail-section-${s.order}`}
                className={`w-full text-left px-space-3 py-space-2 border rounded-md text-small flex items-center gap-space-2 ${
                  key === activeKey
                    ? "bg-gold/15 border-gold text-onyx"
                    : "bg-white-pure border-border-light text-onyx hover:bg-white-warm/60"
                }`}
              >
                <StatusDot status={status} />
                <span className="flex-1 min-w-0 truncate">{s.title}</span>
                {unresolvedComments > 0 && (
                  <span
                    className="rounded-full bg-status-warning/10 px-space-2 py-[1px] text-[11px] font-semibold text-status-warning"
                    data-testid={`wizard-rail-changes-${s.order}`}
                  >
                    Changes requested
                  </span>
                )}
                <span className="text-caption text-gray-mid shrink-0">{statusLabel(status)}</span>
              </button>
            </li>
          );
        })}
        <li>
          <button
            type="button"
            onClick={() => onJump(REVIEW_SECTION_KEY)}
            aria-current={REVIEW_SECTION_KEY === activeKey ? "step" : undefined}
            data-testid="wizard-rail-review"
            className={`w-full text-left px-space-3 py-space-2 border rounded-md text-small truncate ${
              REVIEW_SECTION_KEY === activeKey
                ? "bg-gold/15 border-gold text-onyx"
                : "bg-white-pure border-border-light text-onyx hover:bg-white-warm/60"
            }`}
          >
            Review &amp; Submit
          </button>
        </li>
      </ol>
    </nav>
  );
}

type DisplayStatus = "NotStarted" | "InProgress" | "Complete";

export function sectionDisplayStatus(
  section: DealSubmissionSectionDto,
  data: DealSubmissionRichDto,
): DisplayStatus {
  // Keep the rail aligned with the backend submit gate: a section is only
  // Done when its required upload slots are satisfied, its section state was
  // saved Complete, and every applicable Evaluate & Feedback run is current.
  const reqs = data.documentRequests.filter((r) => r.sectionId === section.id);
  const requiredDocs = reqs.filter((r) => r.required);
  const uploadedRequestIds = new Set(data.documents.map((d) => d.documentRequestId));
  const missingRequiredDocs = requiredDocs.some((r) => !uploadedRequestIds.has(r.id));
  const anyUploadedInSection = reqs.some((r) => uploadedRequestIds.has(r.id));
  const state = data.sectionStates.find((s) => s.sectionId === section.id) ?? null;
  const outstandingEvaluations = outstandingEvaluationNames(section, state, reqs, data.documents);

  if (state?.status === "Complete" && !missingRequiredDocs && outstandingEvaluations.length === 0) {
    return "Complete";
  }

  if (state || anyUploadedInSection || missingRequiredDocs) {
    return "InProgress";
  }

  return "NotStarted";
}

function statusLabel(status: DisplayStatus): string {
  switch (status) {
    case "Complete": return "Done";
    case "InProgress": return "In progress";
    case "NotStarted": return "Not started";
  }
}

function StatusDot({ status }: { status: DisplayStatus }) {
  const tone =
    status === "Complete" ? "bg-status-success" :
    status === "InProgress" ? "bg-gold" :
    "bg-white-warm border border-border-light";
  return (
    <span
      aria-hidden="true"
      className={`inline-block w-2 h-2 rounded-full ${tone}`}
    />
  );
}
